(function () {
  'use strict';

  const MAX_FILE_BYTES = 10 * 1024 * 1024;
  const ALLOWED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
  const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  const TURNSTILE_LOAD_TIMEOUT_MS = 12000;

  function waitForTurnstile(timeoutMs) {
    return new Promise(function (resolve, reject) {
      const startedAt = Date.now();

      function check() {
        if (window.turnstile && typeof window.turnstile.render === 'function') {
          resolve(window.turnstile);
          return;
        }

        if (Date.now() - startedAt >= timeoutMs) {
          reject(new Error('Turnstile did not become ready in time.'));
          return;
        }

        window.setTimeout(check, 50);
      }

      check();
    });
  }

  function loadTurnstile() {
    if (window.turnstile && typeof window.turnstile.render === 'function') {
      return Promise.resolve(window.turnstile);
    }

    if (window.hmTradeApplicationTurnstilePromise) {
      return window.hmTradeApplicationTurnstilePromise;
    }

    window.hmTradeApplicationTurnstilePromise = new Promise(function (resolve, reject) {
      let settled = false;

      function succeed(turnstile) {
        if (settled) return;
        settled = true;
        resolve(turnstile);
      }

      function fail(error) {
        if (settled) return;
        settled = true;
        reject(error);
      }

      function waitUntilReady() {
        waitForTurnstile(TURNSTILE_LOAD_TIMEOUT_MS).then(succeed).catch(fail);
      }

      const existingScript = Array.from(document.scripts).find(function (script) {
        return script.src && script.src.indexOf('https://challenges.cloudflare.com/turnstile/v0/api.js') === 0;
      });

      if (existingScript) {
        existingScript.addEventListener('load', waitUntilReady, { once: true });
        existingScript.addEventListener('error', function () {
          fail(new Error('Turnstile could not load.'));
        }, { once: true });

        // The script may already have completed before this listener was added.
        waitUntilReady();
        return;
      }

      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', waitUntilReady, { once: true });
      script.addEventListener('error', function () {
        fail(new Error('Turnstile could not load.'));
      }, { once: true });
      document.head.appendChild(script);
    }).catch(function (error) {
      // Allow a later retry if the first script load was interrupted.
      window.hmTradeApplicationTurnstilePromise = null;
      throw error;
    });

    return window.hmTradeApplicationTurnstilePromise;
  }

  function setVisible(wrapper, input, visible) {
    if (!wrapper || !input) return;
    wrapper.hidden = !visible;
    input.required = visible;
    if (!visible) {
      input.value = '';
      input.removeAttribute('aria-invalid');
      input.classList.remove('hm-trade-control-invalid');
      input.setCustomValidity('');
    }
  }

  function setBusy(button, busy) {
    button.disabled = busy;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function resetFieldErrors(form) {
    form.querySelectorAll('[aria-invalid="true"]').forEach(function (field) {
      field.removeAttribute('aria-invalid');
      field.classList.remove('hm-trade-control-invalid');
    });
    form.querySelectorAll('.hm-trade-validation-highlight').forEach(function (container) {
      container.classList.remove('hm-trade-validation-highlight');
      container.removeAttribute('data-validation-error');
    });
    form.querySelectorAll('[data-field-error]').forEach(function (message) {
      message.remove();
    });
    form.querySelectorAll('input, select, textarea').forEach(function (field) {
      field.setCustomValidity('');
      const describedBy = (field.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter(function (id) { return id && !id.startsWith('HmTradeError-'); });
      if (describedBy.length) {
        field.setAttribute('aria-describedby', describedBy.join(' '));
      } else {
        field.removeAttribute('aria-describedby');
      }
    });
  }

  function fieldErrorContainer(field) {
    return field.closest('.hm-trade-field, .hm-trade-upload, .hm-trade-question, .hm-trade-check-row');
  }

  function updateErrorSummary(form, summary) {
    if (!summary) return;

    const count = form.querySelectorAll('.hm-trade-validation-highlight').length;
    if (count === 0) {
      summary.hidden = true;
      return;
    }

    const messageElement = summary.querySelector('[data-error-message]');
    if (messageElement) {
      messageElement.textContent = count === 1
        ? 'There is 1 highlighted field to complete below.'
        : 'There are ' + count + ' highlighted fields to complete below.';
    }
    summary.hidden = false;
  }

  function clearFieldError(field, summary) {
    const container = fieldErrorContainer(field);
    if (!container || !container.classList.contains('hm-trade-validation-highlight')) return;

    container.classList.remove('hm-trade-validation-highlight');
    container.removeAttribute('data-validation-error');
    container.querySelectorAll('[aria-invalid="true"]').forEach(function (invalidField) {
      invalidField.removeAttribute('aria-invalid');
      invalidField.classList.remove('hm-trade-control-invalid');
      invalidField.setCustomValidity('');

      const describedBy = (invalidField.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter(function (id) { return id && !id.startsWith('HmTradeError-'); });
      if (describedBy.length) {
        invalidField.setAttribute('aria-describedby', describedBy.join(' '));
      } else {
        invalidField.removeAttribute('aria-describedby');
      }
    });
    container.querySelectorAll('.hm-trade-control-invalid').forEach(function (invalidField) {
      invalidField.classList.remove('hm-trade-control-invalid');
    });
    container.querySelectorAll('[data-field-error]').forEach(function (message) {
      message.remove();
    });
    updateErrorSummary(field.form, summary);
  }

  function showFieldError(field, message) {
    if (!field) return;

    field.setAttribute('aria-invalid', 'true');
    field.classList.add('hm-trade-control-invalid');
    const container = fieldErrorContainer(field);
    if (!container) return;
    container.classList.add('hm-trade-validation-highlight');
    container.setAttribute('data-validation-error', 'true');

    const existingError = container.querySelector('[data-field-error]');
    if (existingError) return container;

    const error = document.createElement('p');
    const fieldId = field.id || field.name || 'field';
    error.id = 'HmTradeError-' + fieldId.replace(/[^a-zA-Z0-9_-]/g, '-');
    error.className = 'hm-trade-field__error';
    error.dataset.fieldError = 'true';
    error.textContent = message;
    container.appendChild(error);

    const describedBy = (field.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter(Boolean);
    if (!describedBy.includes(error.id)) describedBy.push(error.id);
    field.setAttribute('aria-describedby', describedBy.join(' '));

    return container;
  }

  function validationMessageFor(field) {
    if (field.validity && field.validity.customError && field.validationMessage) {
      return field.validationMessage;
    }

    if (field.validity && field.validity.typeMismatch && field.name === 'email') {
      return 'Please enter a valid email address.';
    }

    const requiredMessages = {
      first_name: 'Please enter your first name.',
      last_name: 'Please enter your last name.',
      email: 'Please enter your email address.',
      mobile_phone: 'Please enter your mobile phone number.',
      street_address: 'Please enter your street address.',
      city: 'Please enter your city or town.',
      postcode: 'Please enter your postcode.',
      country: 'Please select your country.',
      country_other: 'Please enter your country.',
      region: 'Please select your region.',
      region_other: 'Please enter your state or region.',
      applicant_type: 'Please choose the option that best describes you.',
      training_institute: 'Please enter where you are training.',
      applicant_type_other: 'Please tell us what best describes you.',
      services: 'Please choose at least one service.',
      proof_file: 'Please upload one proof-of-trade file, or select the experience option below.',
      experience_summary: 'Please describe your trade experience so we can assess your application.'
    };

    return requiredMessages[field.name] || field.validationMessage || 'Please complete this required field.';
  }

  function showValidationErrors(form, summary, errors) {
    const shownContainers = new Set();

    errors.forEach(function (error) {
      const container = fieldErrorContainer(error.field);
      if (container && shownContainers.has(container)) return;
      if (container) shownContainers.add(container);
      showFieldError(error.field, error.message);
    });

    updateErrorSummary(form, summary);

    if (!errors.length) return;
    const firstField = errors[0].field;
    const firstContainer = fieldErrorContainer(firstField);
    firstField.focus({ preventScroll: true });
    (firstContainer || firstField).scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function resetErrorSummary(summary) {
    if (!summary) return;
    const heading = summary.querySelector('[data-error-heading]');
    const support = summary.querySelector('[data-error-support]');
    const reference = summary.querySelector('[data-error-reference]');
    if (heading) heading.textContent = 'Please check the highlighted fields.';
    if (support) support.hidden = true;
    if (reference) reference.textContent = '';
  }

  function showError(form, summary, message, field) {
    resetErrorSummary(summary);
    const clearMessage = message || 'Please complete every required field.';
    const messageElement = summary.querySelector('[data-error-message]');
    if (messageElement) messageElement.textContent = clearMessage;
    summary.hidden = false;
    if (field) {
      const highlightedContainer = showFieldError(field, clearMessage);
      field.focus({ preventScroll: true });
      (highlightedContainer || field).scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      summary.focus({ preventScroll: true });
      summary.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function showSystemError(form, summary, message, referenceCode) {
    resetErrorSummary(summary);
    const heading = summary.querySelector('[data-error-heading]');
    const messageElement = summary.querySelector('[data-error-message]');
    const support = summary.querySelector('[data-error-support]');
    const reference = summary.querySelector('[data-error-reference]');
    if (heading) heading.textContent = "We couldn't submit your application.";
    if (messageElement) messageElement.textContent = message || 'Please try again.';
    if (reference) reference.textContent = referenceCode || 'TA-SYSTEM';
    if (support) support.hidden = false;
    summary.hidden = false;
    summary.focus({ preventScroll: true });
    summary.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function validateForm(form) {
    resetFieldErrors(form);

    const services = Array.from(form.querySelectorAll('[data-service]'));
    const firstService = services[0];
    if (firstService && !services.some(function (field) { return field.checked; })) {
      firstService.setCustomValidity('Please choose at least one service.');
    }

    const noDocuments = form.querySelector('[data-no-documents]');
    const proofFile = form.querySelector('[data-proof-file]');
    const experienceInput = form.querySelector('[data-experience]');
    const hasNoDocuments = Boolean(noDocuments && noDocuments.checked);
    if (proofFile) proofFile.required = !hasNoDocuments;

    if (hasNoDocuments && experienceInput) {
      const characterCount = experienceInput.value.trim().length;
      if (characterCount === 0) {
        experienceInput.setCustomValidity('Please describe your trade experience so we can assess your application.');
      } else if (characterCount < 30) {
        experienceInput.setCustomValidity(
          'Please enter at least 30 characters describing your trade experience. You have entered ' +
          characterCount +
          (characterCount === 1 ? ' character.' : ' characters.')
        );
      }
    }

    if (proofFile && proofFile.files.length > 1) {
      proofFile.setCustomValidity('Please upload one file only.');
    }

    if (proofFile && proofFile.files.length === 1) {
      const file = proofFile.files[0];
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        proofFile.setCustomValidity('Please upload a PDF, JPG or PNG file.');
      } else if (file.size > MAX_FILE_BYTES) {
        proofFile.setCustomValidity('The proof-of-trade file must be 10 MB or smaller.');
      }
    }

    const invalidFields = Array.from(form.querySelectorAll('input, select, textarea'))
      .filter(function (field) {
        return field.willValidate && !field.validity.valid;
      });

    if (invalidFields.length) {
      return {
        valid: false,
        errors: invalidFields.map(function (field) {
          return { field: field, message: validationMessageFor(field) };
        })
      };
    }

    return { valid: true };
  }

  function initSection(section) {
    if (section.dataset.hmTradeInitialised === 'true') return;
    section.dataset.hmTradeInitialised = 'true';

    const form = section.querySelector('[data-trade-form]');
    if (!form) return;

    const endpoint = form.dataset.endpoint;
    const errorSummary = form.querySelector('[data-error-summary]');
    const submitButton = form.querySelector('[data-submit-button]');
    const successPanel = section.querySelector('[data-success-panel]');
    const startedAt = form.querySelector('[data-started-at]');
    const country = form.querySelector('[data-country]');
    const countryOtherWrap = form.querySelector('[data-country-other-wrap]');
    const countryOther = form.querySelector('[data-country-other]');
    const regionNzWrap = form.querySelector('[data-region-nz-wrap]');
    const regionNz = form.querySelector('[data-region-nz]');
    const regionOtherWrap = form.querySelector('[data-region-other-wrap]');
    const regionOther = form.querySelector('[data-region-other]');
    const trainingWrap = form.querySelector('[data-training-wrap]');
    const trainingInput = form.querySelector('[data-training-input]');
    const applicantOtherWrap = form.querySelector('[data-applicant-other-wrap]');
    const applicantOtherInput = form.querySelector('[data-applicant-other-input]');
    const noDocuments = form.querySelector('[data-no-documents]');
    const experienceWrap = form.querySelector('[data-experience-wrap]');
    const experienceInput = form.querySelector('[data-experience]');
    const proofFile = form.querySelector('[data-proof-file]');
    const uploadWrap = form.querySelector('[data-upload-wrap]');
    const fileLabel = form.querySelector('[data-file-label]');
    const turnstileWrap = form.querySelector('[data-turnstile-wrap]');
    const turnstileContainer = form.querySelector('[data-turnstile-widget]');
    const turnstileStatus = form.querySelector('[data-turnstile-status]');
    const turnstileSiteKey = turnstileContainer ? String(turnstileContainer.dataset.sitekey || '').trim() : '';
    let turnstileApi = null;
    let turnstileWidgetId = null;
    let turnstileToken = '';
    let turnstileState = turnstileSiteKey ? 'loading' : 'disabled';
    let preserveTurnstileError = false;

    if (startedAt) startedAt.value = new Date().toISOString();

    function setTurnstileStatus(message, state) {
      if (!turnstileStatus) return;
      const text = turnstileStatus.querySelector('[data-turnstile-status-text]');
      const icon = turnstileStatus.querySelector('[data-turnstile-status-icon]');
      const resolvedState = state || 'loading';

      turnstileStatus.classList.remove('is-loading', 'is-success', 'is-error');
      turnstileStatus.classList.add('is-' + resolvedState);
      if (text) text.textContent = message || '';
      if (icon) icon.textContent = resolvedState === 'success' ? '✓' : (resolvedState === 'error' ? '!' : '…');
      turnstileStatus.hidden = !message;
    }

    function clearTurnstileError() {
      if (!turnstileWrap) return;
      const hadTurnstileError = turnstileWrap.classList.contains('hm-trade-validation-highlight')
        || Boolean(turnstileWrap.querySelector('[data-turnstile-error]'));
      if (!hadTurnstileError) return;

      turnstileWrap.classList.remove('hm-trade-validation-highlight');
      turnstileWrap.removeAttribute('data-validation-error');
      turnstileWrap.querySelectorAll('[data-turnstile-error]').forEach(function (message) {
        message.remove();
      });
      updateErrorSummary(form, errorSummary);
    }

    function showTurnstileError(message, shouldFocus) {
      if (!turnstileWrap) {
        showError(form, errorSummary, message);
        return;
      }

      clearTurnstileError();
      turnstileWrap.classList.add('hm-trade-validation-highlight');
      turnstileWrap.setAttribute('data-validation-error', 'true');

      const error = document.createElement('p');
      error.className = 'hm-trade-field__error';
      error.dataset.fieldError = 'true';
      error.dataset.turnstileError = 'true';
      error.textContent = message;
      turnstileWrap.appendChild(error);
      updateErrorSummary(form, errorSummary);

      if (shouldFocus !== false) {
        if (!turnstileWrap.hasAttribute('tabindex')) turnstileWrap.setAttribute('tabindex', '-1');
        turnstileWrap.focus({ preventScroll: true });
        turnstileWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    function resetTurnstile() {
      turnstileToken = '';
      if (!turnstileApi || turnstileWidgetId === null) return;

      turnstileState = 'loading';
      setTurnstileStatus('', 'loading');
      try {
        turnstileApi.reset(turnstileWidgetId);
      } catch (_error) {
        turnstileState = 'error';
      }
    }

    async function initialiseTurnstile() {
      if (!turnstileContainer || !turnstileSiteKey) return;

      turnstileState = 'loading';
      setTurnstileStatus('', 'loading');
      try {
        turnstileApi = await loadTurnstile();
        if (!turnstileContainer.isConnected || turnstileWidgetId !== null) return;

        turnstileWidgetId = turnstileApi.render(turnstileContainer, {
          sitekey: turnstileSiteKey,
          theme: 'light',
          size: 'normal',
          appearance: 'always',
          execution: 'render',
          action: 'trade_application',
          'response-field': false,
          callback: function (token) {
            turnstileToken = token || '';
            turnstileState = turnstileToken ? 'ready' : 'error';
            setTurnstileStatus(
              turnstileToken ? '' : 'The security check could not complete. Please try again.',
              turnstileToken ? 'success' : 'error'
            );
            if (turnstileToken && !preserveTurnstileError) clearTurnstileError();
          },
          'expired-callback': function () {
            turnstileToken = '';
            turnstileState = 'expired';
            setTurnstileStatus('The security check expired. Please complete it again before submitting.', 'error');
          },
          'error-callback': function () {
            turnstileToken = '';
            turnstileState = 'error';
            setTurnstileStatus('The security check could not complete. Please retry it before submitting.', 'error');
          }
        });
      } catch (_error) {
        turnstileToken = '';
        turnstileState = 'error';
        setTurnstileStatus('The security check could not load. Please refresh the page and try again.', 'error');
      }
    }

    function updateCountry() {
      const isNewZealand = Boolean(country && country.value === 'New Zealand');
      const isOther = Boolean(country && country.value === 'Other');
      setVisible(countryOtherWrap, countryOther, isOther);
      setVisible(regionNzWrap, regionNz, isNewZealand);
      setVisible(regionOtherWrap, regionOther, isOther);
      if (!isOther && countryOther) {
        clearFieldError(countryOther, errorSummary);
      }
      if (!isNewZealand && regionNz) clearFieldError(regionNz, errorSummary);
      if (!isOther && regionOther) clearFieldError(regionOther, errorSummary);
    }

    function updateApplicantType() {
      const selected = form.querySelector('input[name="applicant_type"]:checked');
      const value = selected ? selected.value : '';
      setVisible(trainingWrap, trainingInput, value === 'student');
      setVisible(applicantOtherWrap, applicantOtherInput, value === 'other');
      if (value !== 'student' && trainingInput) clearFieldError(trainingInput, errorSummary);
      if (value !== 'other' && applicantOtherInput) clearFieldError(applicantOtherInput, errorSummary);
    }

    function updateProofRoute() {
      const useExplanation = Boolean(noDocuments && noDocuments.checked);
      setVisible(experienceWrap, experienceInput, useExplanation);
      if (proofFile) {
        proofFile.required = !useExplanation;
        proofFile.disabled = useExplanation;
        if (useExplanation) proofFile.value = '';
      }
      if (useExplanation && proofFile) clearFieldError(proofFile, errorSummary);
      if (!useExplanation && experienceInput) clearFieldError(experienceInput, errorSummary);
      if (uploadWrap) uploadWrap.classList.toggle('is-disabled', useExplanation);
      if (fileLabel) {
        fileLabel.textContent = useExplanation
          ? 'Document upload is not required when providing an experience explanation.'
          : 'PDF, JPG or PNG, maximum 10 MB.';
      }
    }

    if (country) country.addEventListener('change', updateCountry);
    form.querySelectorAll('input[name="applicant_type"]').forEach(function (field) {
      field.addEventListener('change', updateApplicantType);
    });
    if (noDocuments) noDocuments.addEventListener('change', updateProofRoute);
    if (proofFile) {
      proofFile.addEventListener('change', function () {
        if (!fileLabel) return;
        fileLabel.textContent = proofFile.files.length === 1
          ? proofFile.files[0].name
          : 'PDF, JPG or PNG, maximum 10 MB.';
      });
    }

    function clearCorrectedError(field) {
      if (field.matches('[data-service]')) {
        const hasService = Array.from(form.querySelectorAll('[data-service]'))
          .some(function (service) { return service.checked; });
        if (hasService) clearFieldError(field, errorSummary);
        return;
      }

      if (field.name === 'applicant_type') {
        if (form.querySelector('input[name="applicant_type"]:checked')) {
          clearFieldError(field, errorSummary);
        }
        return;
      }

      if (field.matches('[data-experience]') && noDocuments && noDocuments.checked) {
        if (field.value.trim().length >= 30) clearFieldError(field, errorSummary);
        return;
      }

      if (field.matches('[data-proof-file]')) {
        if (field.files.length !== 1) return;
        const file = field.files[0];
        if (ALLOWED_FILE_TYPES.includes(file.type) && file.size <= MAX_FILE_BYTES) {
          clearFieldError(field, errorSummary);
        }
        return;
      }

      field.setCustomValidity('');
      if (field.validity.valid) clearFieldError(field, errorSummary);
    }

    form.addEventListener('input', function (event) {
      if (event.target.matches('input, select, textarea')) clearCorrectedError(event.target);
    });
    form.addEventListener('change', function (event) {
      if (event.target.matches('input, select, textarea')) clearCorrectedError(event.target);
    });

    updateCountry();
    updateApplicantType();
    updateProofRoute();
    initialiseTurnstile();

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      preserveTurnstileError = false;
      resetErrorSummary(errorSummary);
      errorSummary.hidden = true;

      if (!endpoint) {
        showSystemError(
          form,
          errorSummary,
          'The secure application service is temporarily unavailable.',
          'TA-CONFIG'
        );
        return;
      }

      const validation = validateForm(form);
      if (!validation.valid) {
        showValidationErrors(form, errorSummary, validation.errors);
        return;
      }

      if (turnstileSiteKey) {
        if (!turnstileToken && turnstileApi && turnstileWidgetId !== null) {
          try {
            turnstileToken = turnstileApi.getResponse(turnstileWidgetId) || '';
            if (turnstileToken) turnstileState = 'ready';
          } catch (_error) {
            turnstileToken = '';
          }
        }

        if (!turnstileToken) {
          const message = turnstileState === 'loading'
            ? 'Please wait for the security check to complete, then submit your application again.'
            : 'Please complete the security check before submitting your application.';
          showTurnstileError(message, true);
          return;
        }
      }

      setBusy(submitButton, true);

      try {
        const formData = new FormData(form);
        if (turnstileToken) formData.set('cf-turnstile-response', turnstileToken);

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: formData
        });

        let payload = {};
        try {
          payload = await response.json();
        } catch (parseError) {
          payload = {};
        }

        if (!response.ok) {
          const submissionError = new Error(payload.message || 'We could not submit your application. Please check your details and try again.');
          submissionError.fieldName = payload.field;
          submissionError.httpStatus = response.status;
          submissionError.isTurnstile = response.status === 400 && /spam check|security check/i.test(submissionError.message);
          throw submissionError;
        }

        const heading = successPanel.querySelector('[data-success-heading]');
        const copy = successPanel.querySelector('[data-success-copy]');
        const reference = successPanel.querySelector('[data-application-reference]');
        const action = successPanel.querySelector('a');
        if (heading) {
          heading.textContent = payload.title || form.dataset.successTitle;
        }
        if (copy) {
          copy.textContent = payload.message || form.dataset.successMessage;
        }
        if (reference && payload.reference) reference.textContent = 'Application reference: ' + payload.reference;
        if (action && payload.actionLabel) action.textContent = payload.actionLabel;
        if (action && payload.actionUrl) action.href = payload.actionUrl;

        form.hidden = true;
        successPanel.hidden = false;
        successPanel.focus({ preventScroll: true });
        successPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (error) {
        if (error.isTurnstile) {
          preserveTurnstileError = true;
          resetTurnstile();
          showTurnstileError('The security check could not be verified. Please complete it again and resubmit your application. If it keeps failing, please contact sales@hairandmore.co.nz.', true);
          return;
        }

        const field = error.fieldName
          ? form.querySelector('[name="' + CSS.escape(error.fieldName) + '"]')
          : null;
        const httpStatus = Number(error.httpStatus || 0);

        if (error && error.name === 'TypeError') {
          showSystemError(
            form,
            errorSummary,
            'We could not connect to the secure application service. Please check your connection and try again.',
            'TA-NETWORK'
          );
        } else if (httpStatus === 429) {
          showSystemError(
            form,
            errorSummary,
            error.message || 'Too many application attempts have been received from this connection. Please try again later.',
            'TA-RATE-LIMIT'
          );
        } else if (httpStatus >= 500) {
          showSystemError(
            form,
            errorSummary,
            error.message || 'The secure application service could not save your application. Please try again.',
            'TA-SERVER'
          );
        } else {
          showError(
            form,
            errorSummary,
            error.message || 'We could not submit your application. Please check your details and try again.',
            field
          );
        }
        resetTurnstile();
      } finally {
        setBusy(submitButton, false);
      }
    });
  }

  function initAll(root) {
    root.querySelectorAll('[data-hm-trade-application]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
